/**
 * Viewport touch gestures → camera navigation, selection and history, as
 * mobile 3D tools do them (docs/planning/07-mobile-editor.md):
 *
 * - One finger dragged: orbit around the focus point (Alt + LMB drag)
 * - Two fingers moved together: pan (MMB drag); spread or pinched: zoom (wheel)
 * - Tap: select what's under the finger, or clear (LMB click)
 * - Double-tap: frame what it lands on (F)
 * - Long press: select it and open its context menu (RMB click); over empty
 *   space, the viewport's menu (Reset view)
 * - Two-finger tap: undo; three-finger tap: redo (as Procreate)
 *
 * The mouse, pen and keyboard stay with ViewportNavigator: the viewport routes
 * each pointer by its type. Like that navigator, this is DOM-agnostic and
 * unit-tested with plain objects; camera motion is ephemeral view state
 * (A-16), and selection, framing and history go through the Command Bus.
 */
import type { CommandRequest } from "../../commands/types";
import type { ReadableStore } from "../../core/store";
import type { CancelTimer, Timers } from "../../core/Timers";
import type { EntityId } from "../../world/entity";
import type { SelectionState } from "../selection/Selection";
import { orbit, pan, zoom } from "./cameraMath";
import type { ViewportCamera } from "./ViewportCamera";

/** A finger: which one, where on the page, and when (ms, the event's timeStamp). */
export interface TouchLike {
  readonly pointerId: number;
  readonly clientX: number;
  readonly clientY: number;
  readonly timeStamp: number;
}

export interface TouchRect {
  readonly left: number;
  readonly top: number;
}

export interface TouchNavigatorOptions {
  readonly camera: ViewportCamera;
  readonly timers: Timers;
  readonly dispatch: (request: CommandRequest) => unknown;
  /** The entity under a viewport point (CSS px from the viewport's top-left). */
  readonly pick: (x: number, y: number) => Promise<EntityId | null>;
  readonly selection: ReadableStore<SelectionState>;
  /** Called on any camera navigation (the nav hint hides after the first one). */
  readonly onNavigated: () => void;
  /** A long press on an entity (now selected): open its context menu at this page point. */
  readonly onLongPress: (at: { x: number; y: number }) => void;
  /** A long press on empty space: open the viewport's own menu (Reset view) at this page point. */
  readonly onViewMenu: (at: { x: number; y: number }) => void;
}

/** Held this long without moving, a press opens the context menu (iOS's and Android's long press). */
export const LONG_PRESS_MS = 500;
/** A second tap within this long, and this close, of the first is a double-tap. */
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_PX = 24;
/** A finger that moves farther than this is dragging, not tapping (fingers wobble more than mice). */
const TOUCH_SLOP_PX = 10;

type Gesture =
  /** Fingers down, none moved yet: it may still be a tap. */
  | "pending"
  | "orbit"
  | "twoFinger"
  /** Finished early (a long press, or fingers that moved in a three-finger press). */
  | "done";

interface Finger {
  readonly start: { readonly x: number; readonly y: number };
  x: number;
  y: number;
}

export class TouchNavigator {
  private readonly fingers = new Map<number, Finger>();
  private gesture: Gesture = "pending";
  /** The most fingers down at once during this gesture: a tap's finger count. */
  private mostFingers = 0;
  private cancelLongPress: CancelTimer | null = null;
  private lastTap: { x: number; y: number; time: number } | null = null;

  constructor(private readonly options: TouchNavigatorOptions) {}

  /** True while fingers are down: a second finger joins the gesture wherever it lands. */
  get active(): boolean {
    return this.fingers.size > 0;
  }

  down(touch: TouchLike, rect: TouchRect): void {
    if (this.fingers.size === 0) {
      this.gesture = "pending";
      this.mostFingers = 0;
    }
    this.fingers.set(touch.pointerId, {
      start: { x: touch.clientX, y: touch.clientY },
      x: touch.clientX,
      y: touch.clientY,
    });
    this.mostFingers = Math.max(this.mostFingers, this.fingers.size);

    // A long press is one finger held still; a second finger makes it something else.
    this.stopLongPress();
    if (this.fingers.size === 1) this.startLongPress(touch, rect);
  }

  move(touch: TouchLike): void {
    const finger = this.fingers.get(touch.pointerId);
    if (!finger || this.gesture === "done") return;

    const before = this.fingerPositions();
    finger.x = touch.clientX;
    finger.y = touch.clientY;
    if (this.gesture === "pending" && !this.movedPastSlop()) return;

    this.stopLongPress();
    if (this.gesture === "pending") this.gesture = this.gestureFor(this.fingers.size);
    if (this.gesture === "orbit")
      this.orbitBy(
        touch.clientX - (before[0]?.x ?? touch.clientX),
        touch.clientY - (before[0]?.y ?? touch.clientY),
      );
    if (this.gesture === "twoFinger") this.twoFingerFrom(before);
  }

  /** Lifts a finger. When the last one lifts, a gesture that never moved is a tap. */
  up(touch: TouchLike, rect: TouchRect): void {
    if (!this.fingers.delete(touch.pointerId)) return;
    this.stopLongPress();
    if (this.fingers.size > 0) {
      // Lifting one finger of a pan leaves the other: it mustn't turn into an orbit.
      if (this.gesture === "twoFinger") this.gesture = "done";
      return;
    }
    if (this.gesture !== "pending") return;

    if (this.mostFingers === 2) this.run("history.undo");
    else if (this.mostFingers === 3) this.run("history.redo");
    else if (this.mostFingers === 1) void this.tap(touch, rect);
  }

  /** The browser took the touch (a system gesture, or the page lost focus): nothing happens. */
  cancel(touch: TouchLike): void {
    this.fingers.delete(touch.pointerId);
    this.stopLongPress();
    this.gesture = "done";
  }

  private gestureFor(fingers: number): Gesture {
    if (fingers === 1) return "orbit";
    if (fingers === 2) return "twoFinger";
    return "done";
  }

  private async tap(touch: TouchLike, rect: TouchRect): Promise<void> {
    const at = { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    const double = this.isDoubleTap(at, touch.timeStamp);
    this.lastTap = double ? null : { ...at, time: touch.timeStamp };

    const hit = await this.options.pick(at.x, at.y);
    this.options.dispatch({
      type: "selection.set",
      payload: { ids: hit ? [hit] : [] },
      origin: "viewport",
    });
    if (double && hit) {
      this.options.dispatch({ type: "camera.frame", payload: {}, origin: "viewport" });
    }
  }

  private isDoubleTap(at: { x: number; y: number }, time: number): boolean {
    const last = this.lastTap;
    if (!last) return false;
    return (
      time - last.time <= DOUBLE_TAP_MS && Math.hypot(at.x - last.x, at.y - last.y) <= DOUBLE_TAP_PX
    );
  }

  private startLongPress(touch: TouchLike, rect: TouchRect): void {
    const page = { x: touch.clientX, y: touch.clientY };
    this.cancelLongPress = this.options.timers.after(LONG_PRESS_MS, () => {
      this.cancelLongPress = null;
      if (this.gesture !== "pending" || this.fingers.size !== 1) return;
      this.gesture = "done";
      void this.longPress(page, rect);
    });
  }

  private async longPress(page: { x: number; y: number }, rect: TouchRect): Promise<void> {
    const hit = await this.options.pick(page.x - rect.left, page.y - rect.top);
    if (!hit) {
      this.options.onViewMenu(page);
      return;
    }
    // On a selected entity the menu acts on the whole selection, as a right-click's does.
    if (!this.options.selection.getSnapshot().ids.includes(hit)) {
      this.options.dispatch({ type: "selection.set", payload: { ids: [hit] }, origin: "viewport" });
    }
    this.options.onLongPress(page);
  }

  private stopLongPress(): void {
    this.cancelLongPress?.();
    this.cancelLongPress = null;
  }

  private orbitBy(dx: number, dy: number): void {
    this.navigate(() => {
      this.options.camera.navigate((camera) => orbit(camera, dx, dy));
    });
  }

  /** Two fingers: the midpoint's travel pans, the change in their spread zooms. */
  private twoFingerFrom(before: readonly { x: number; y: number }[]): void {
    const after = this.fingerPositions();
    const [a0, b0] = before;
    const [a1, b1] = after;
    if (!a0 || !b0 || !a1 || !b1) return;

    const dx = (a1.x + b1.x - a0.x - b0.x) / 2;
    const dy = (a1.y + b1.y - a0.y - b0.y) / 2;
    const spreadBefore = Math.hypot(b0.x - a0.x, b0.y - a0.y);
    const spreadAfter = Math.hypot(b1.x - a1.x, b1.y - a1.y);
    const { camera } = this.options;

    this.navigate(() => {
      camera.navigate((view) => {
        const panned = pan(view, dx, dy, camera.viewportSize.height);
        return spreadBefore > 0 ? zoom(panned, spreadAfter / spreadBefore) : panned;
      });
    });
  }

  private movedPastSlop(): boolean {
    for (const finger of this.fingers.values()) {
      const moved = Math.hypot(finger.x - finger.start.x, finger.y - finger.start.y);
      if (moved > TOUCH_SLOP_PX) return true;
    }
    return false;
  }

  /** The fingers' positions, in the order they went down. */
  private fingerPositions(): { x: number; y: number }[] {
    return [...this.fingers.values()].map(({ x, y }) => ({ x, y }));
  }

  private run(type: "history.undo" | "history.redo"): void {
    this.options.dispatch({ type, payload: {}, origin: "viewport" });
  }

  private navigate(move: () => void): void {
    move();
    this.options.onNavigated();
  }
}
